import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, IconButton, Button, Grid } from '@material-ui/core';
import { Typography, Icon } from '@components';
import { content } from '@content';

import { useStyles } from './SelectEntity.styles';
import { SelectEntityProps } from './SelectEntity.props';
import { variables } from '@styles';

/**
 * SelectEntity component (Mindset Info Block)
 * @returns {JSX.Element}
 */
export const SelectEntity = ({
  name,
  items,
  onRemove,
  style,
  splitterId,
  experienceId,
  onClickArrow,
  onClickShowMore,
  moduleHeight,
  currentPosition,
}: SelectEntityProps): JSX.Element => {
  const [currentValue, setCurrent] = useState<number>(currentPosition);
  const [showButton, setShowButton] = useState<boolean>(false);
  const [heightDifference, setHeightDifference] = useState<number>(0);
  const fullBlock = useRef<HTMLDivElement>(null);

  const styles = useStyles({ moduleHeight });

  // setting show/hide Show More button, calculating height difference
  useEffect(() => {
    const elementHeight = fullBlock.current?.clientHeight;

    if (elementHeight && elementHeight > moduleHeight) {
      setShowButton(true);
      setHeightDifference(elementHeight - moduleHeight);
    } else {
      setShowButton(false);
      setHeightDifference(0);
    }
  }, [moduleHeight, currentValue]);

  // changing value of next/previous buttons
  const changeValue = useCallback(
    (value) => {
      if (items.length - 1 === currentValue && value > 0) {
        setCurrent(0);
      } else if (currentValue === 0 && value === -1) {
        setCurrent(items.length - 1);
      } else if (currentValue + value >= 0) {
        setCurrent(currentValue + value);
      }
    },
    [items.length, currentValue, setCurrent],
  );

  const selectEntity = (
    <>
      <Typography.Display>{items[currentValue].title}</Typography.Display>
      <Grid container spacing={2} className={styles.grid}>
        {items[currentValue] &&
          items[currentValue].items.map((item) => (
            <Grid key={item.label} item xs={6}>
              <Typography.SmallCaption className={styles.label}>{item.label}</Typography.SmallCaption>
              <Typography.Headline noWrap>{item.value}</Typography.Headline>
            </Grid>
          ))}
      </Grid>
      <Divider />
      <Box className={styles.row}>
        <Box>
          <IconButton
            className={styles.prevButton}
            onClick={() => {
              changeValue(-1);
              onClickArrow(splitterId, experienceId, 'prev');
            }}
          >
            <Icon.ArrowPrev stroke={variables.color.primary.darkestGray} width={18} height={18} />
          </IconButton>
          <IconButton
            className={styles.nextButton}
            onClick={() => {
              changeValue(+1);
              onClickArrow(splitterId, experienceId, 'next');
            }}
          >
            <Icon.ArrowNext stroke={variables.color.primary.darkestGray} width={18} height={18} />
          </IconButton>
          <Typography.Caption className={styles.counts}>
            <b>{currentValue + 1}</b> {content.of} <b>{items.length}</b> {name}
          </Typography.Caption>
        </Box>
        <IconButton className={styles.removeButton} onClick={onRemove}>
          <Icon.TrashOutline width={18} height={18} />
        </IconButton>
      </Box>
    </>
  );

  return (
    <>
      {showButton ? (
        <Box className={styles.container} style={style}>
          {selectEntity}
          <Box className={styles.showMoreBlock}>
            <Button
              className={styles.showMoreButton}
              variant="outlined"
              color="primary"
              onClick={() => {
                setShowButton(false);
                onClickShowMore(splitterId, heightDifference);
              }}
            >
              {content.showMore}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className={styles.fullContainer} style={style} {...{ ref: fullBlock }}>
          {selectEntity}
        </Box>
      )}
    </>
  );
};
